import React, { useState } from 'react'
import { useQuery, useMutation } from 'react-query'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { useForm } from 'src/forms'
import * as yup from 'yup'

import { DropzoneDialog } from 'material-ui-dropzone'
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { CloudUpload } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { Helmet } from 'react-helmet'

import { ProfileImageEditDialog } from 'src/components/Dialog'

import { setApiErrors } from 'src/forms/errors'
import { Profile } from 'src/api/models'

import { skuit } from 'src/services/skuit'
import useProfileContext from 'src/context/ProfileContext'

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    marginBottom: theme.spacing(6),
  },
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  formField: {
    margin: theme.spacing(2),
  },
  formButton: {
    margin: theme.spacing(2),
  },
  profileImage: {
    height: '120px',
    width: '120px',
    margin: `0 auto ${theme.spacing(2)}px`,
  },
  centered: {
    textAlign: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(2),
  },
}))

interface UpdateProfileProps {
  first_name: string //  eslint-disable-line camelcase
  last_name: string //  eslint-disable-line camelcase
}

const updateProfileSchema = yup.object().shape({
  first_name: yup.string().label('First Name'),
  last_name: yup.string().label('Last Name'),
})

interface ProfileProps {
  profile: {
    data?: Profile
    refetch: () => void
  }
}

const PersonalInformation = (props: ProfileProps) => {
  const { profile } = props
  const classes = useStyles()

  const [dropzoneOpen, setDropzoneOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [file, setFile] = useState<File | undefined>(undefined)
  const [image, setImage] = useState<string | undefined>(undefined)

  const { Form, Field, data: formData } = useForm(updateProfileSchema)

  const { isLoading: profileUpdating, mutate: updateProfile } = useMutation(
    skuit.base.profiles.patch,
    {
      onSuccess: () => {
        toast.success(() => 'Successfully updated profile!')
      },
      onError: (error: AxiosError) => {
        setApiErrors(formData.setError, error)
      },
    }
  )

  const onSubmit = (data: UpdateProfileProps) => {
    updateProfile(data)
  }

  const onImageSelect = (files: File[]) => {
    setFile(files[0])
    setDropzoneOpen(false)
    setEditOpen(true)
  }
  const profileContext = useProfileContext()
  const { isLoading: imageLoading, mutate: uploadAvatar } = useMutation(
    skuit.base.profiles.uploadProfileImage,
    {
      onSuccess: () => {
        profile.refetch()
        profileContext.updateProfile(true)
        setEditOpen(false)
      },
    }
  )

  const dataURLtoFile = (dataurl: string, filename: string) => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)![1]
    const bstr = atob(arr[1])
    const u8arr = new Uint8Array(bstr.length)

    for (let i = 0; i < bstr.length; i += 1) {
      u8arr[i] = bstr.charCodeAt(i)
    }

    return new File([u8arr], filename, { type: mime })
  }

  const onImageSave = () => {
    if (!imageLoading && image) {
      const file = dataURLtoFile(image, 'avatar.png')
      uploadAvatar(file)
    }
  }

  const onCrop = async (croppedPicture: string) => {
    setImage(croppedPicture)
  }

  const Profile = useProfileContext()

  return (
    <Card className={classes.section}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Personal Information
        </Typography>
        <Paper>
          <Grid container spacing={6}>
            <Grid item md={8}>
              <Form onSubmit={onSubmit}>
                <Field
                  name="first_name"
                  defaultValue={Profile.profile && Profile.profile?.firstName}
                  renderField={({ name, value, setValue, error }) => (
                    <TextField
                      className={classes.formField}
                      id={name}
                      label="First Name"
                      name={name}
                      disabled={!Profile.profile}
                      error={!!error}
                      helperText={error}
                      fullWidth
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                      value={value || ''}
                    />
                  )}
                />
                <Field
                  name="last_name"
                  defaultValue={Profile.profile && Profile.profile?.lastName}
                  renderField={({ name, value, setValue, error }) => (
                    <TextField
                      className={classes.formField}
                      id={name}
                      label="Last Name"
                      name={name}
                      disabled={!Profile.profile?.lastName}
                      error={!!error}
                      helperText={error}
                      fullWidth
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                      value={value || ''}
                    />
                  )}
                />
                <Button
                  className={classes.formButton}
                  disabled={profileUpdating}
                  type="submit"
                  color="primary"
                  variant="contained"
                >
                  {profileUpdating ? 'Saving...' : 'Save'}
                </Button>
              </Form>
            </Grid>
            <Grid className={classes.centered} item md={4}>
              <Avatar
                className={classes.profileImage}
                alt={Profile.profile?.fullName}
                src={Profile.profile?.profileImage}
              />
              {!!file && (
                <ProfileImageEditDialog
                  image={file}
                  open={!!file && editOpen}
                  handleClose={() => setEditOpen(false)}
                  onCrop={onCrop}
                  onSave={onImageSave}
                />
              )}
              <DropzoneDialog
                open={dropzoneOpen}
                onSave={onImageSelect}
                showPreviews={true}
                filesLimit={1}
                maxFileSize={5000000}
                submitButtonText="Select"
                onClose={() => setDropzoneOpen(false)}
              />
              <label htmlFor="raised-button-file">
                <Button
                  disabled={imageLoading}
                  variant="contained"
                  color="primary"
                  component="span"
                  onClick={() => setDropzoneOpen(true)}
                >
                  <CloudUpload className={classes.buttonIcon} />{' '}
                  {imageLoading ? 'Uploading...' : 'Upload'}
                </Button>

                <Typography variant="caption" display="block" gutterBottom>
                  For best results, use an image at least 128px by 128px in .jpg format
                </Typography>
              </label>
            </Grid>
          </Grid>
        </Paper>
      </CardContent>
    </Card>
  )
}

interface UpdatePasswordProps {
  old_password: string //  eslint-disable-line camelcase
  new_password: string //  eslint-disable-line camelcase
  confirm_new_password: string //  eslint-disable-line camelcase
}

const updatePasswordSchema = yup.object().shape({
  old_password: yup.string().required().label('Old Password'),
  new_password: yup.string().required().label('New Password'),
  confirm_new_password: yup
    .string()
    .required()
    .oneOf([yup.ref('new_password')], 'Passwords must match')
    .label('ConfirmNew Password'),
})

const UpdatePassword = () => {
  const classes = useStyles()

  const { Form, Field, data: formData, clear } = useForm(updatePasswordSchema)

  const { isLoading, mutate: setPassword } = useMutation(skuit.auth.setPassword, {
    onSuccess: () => {
      toast.success(() => 'Successfully updated password!')
      clear()
    },
    onError: (error: AxiosError) => {
      setApiErrors(formData.setError, error)
    },
  })
  const onSubmit = (data: UpdatePasswordProps) => {
    setPassword(data)
  }
  return (
    <Card className={classes.section}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Update Password
        </Typography>
        <Form onSubmit={onSubmit}>
          <Grid container item justify="center" spacing={6}>
            <Grid container item xs={6} spacing={6}>
              <Grid item xs={12}>
                <Field
                  name="old_password"
                  renderField={({ name, value, setValue, error }) => (
                    <TextField
                      className={classes.formField}
                      id={name}
                      label="Old Password"
                      name={name}
                      type="password"
                      autoComplete="current-password"
                      error={!!error}
                      helperText={error}
                      fullWidth
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                      value={value || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="new_password"
                  renderField={({ name, value, setValue, error }) => (
                    <TextField
                      className={classes.formField}
                      id={name}
                      label="New Password"
                      name={name}
                      type="password"
                      error={!!error}
                      helperText={error}
                      fullWidth
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                      value={value || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="confirm_new_password"
                  renderField={({ name, value, setValue, error }) => (
                    <TextField
                      className={classes.formField}
                      id={name}
                      label="Confirm New Password"
                      name={name}
                      type="password"
                      error={!!error}
                      helperText={error}
                      fullWidth
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(name, e.target.value)
                      }
                      value={value || ''}
                    />
                  )}
                />
              </Grid>
              <Grid container item xs={12}>
                <Button
                  disabled={isLoading}
                  className={classes.formButton}
                  type="submit"
                  color="primary"
                  variant="contained"
                >
                  Update Password
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </CardContent>
    </Card>
  )
}

export const SettingsPage = () => {
  const classes = useStyles()
  const profile = useQuery(['profile', 'me'], () => skuit.base.profiles.me())
  return (
    <>
      <Helmet title="Settings" />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="h3" gutterBottom>
            Settings
          </Typography>
          <Divider className={classes.divider} />
          <PersonalInformation profile={profile} />
          <UpdatePassword />
        </Grid>
      </Grid>
    </>
  )
}
